import type { ErrorInfo, FC } from "react"
import { useState } from "react"

import styled from "styled-components"

import waveOverlay from "~/assets/photo/wave-overlay.png"
import IS_DEV from "~/utils/isDev"

import Button from "../Button"

export type FallbackProps = {
  error?: Error | string
  errorInfo?: ErrorInfo
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }
  top: 0;
  left: 0;
  position: absolute;
  overflow: hidden;
  font-family: "Roboto", sans-serif;
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  > img {
    position: absolute;
    top: 0;
    right: 0;
    z-index: -1;
  }
  > div {
    border-radius: 10px;
    padding: 50px;
    > p {
      margin-top: 5px;
    }
    > details {
      font-family: monospace;
      white-space: pre-wrap;
      max-height: 50vh;
      width: 100%;
      overflow-y: auto;
    }
    > form {
      textarea {
        width: 100%;
        height: 200px;
        font-family: "Roboto", sans-serif;
        font-size: 16px;
        border: 1px solid #cfcfcf;
        padding: 10px;
        :focus {
          outline: none;
        }
      }
    }
    > h5 {
      font-family: monospace;
      font-size: 14px;
    }
  }
`
const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  background-color: var(--blue-form-footer);
  width: 100%;
  justify-content: space-between;
  padding: 20px 30px;
  box-sizing: border-box;
  border-radius: 10px;
  > button {
    border: none;
  }
`

export const DefaultFallback: FC<FallbackProps> = ({ error, errorInfo }) => {
  const [showReport, setShowReport] = useState(false)
  const [reportSent, setReportSent] = useState(false)
  const [description, setDescription] = useState(``)

  return (
    <Wrapper>
      <img src={waveOverlay} />
      <div>
        {!showReport || reportSent ? (
          <>
            {reportSent ? (
              <>
                <h1>Thanks for the feedback.</h1>
                <p>Please reload the page to continue.</p>
              </>
            ) : (
              <>
                <h1>Something went wrong.</h1>
                <p>
                  Sorry for the inconvenience. Please reload the page to try
                  again.
                </p>
              </>
            )}
            <div style={{ display: `flex`, gap: `10px`, margin: `20px 0` }}>
              <Button variant="solid" onClick={() => window.location.reload()}>
                Refresh
              </Button>
              {!reportSent && (
                <Button variant="solid" onClick={() => setShowReport(true)}>
                  Report Error
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <h2>Describe what happened before the error:</h2>
            <form>
              <textarea
                autoFocus={true}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <ButtonsContainer
                style={{ backgroundColor: `var(--blue-form-footer)` }}
              >
                <Button variant="default" onClick={() => setShowReport(false)}>
                  Cancel
                </Button>
                <a
                  href={`mailto:support@powur.com?subject=PowurVisionError:${error?.toString()}&body=${description}`}
                  onClick={() => setReportSent(true)}
                >
                  <Button variant="solid">Send</Button>
                </a>
              </ButtonsContainer>
            </form>
          </>
        )}
        <br />
        {IS_DEV && (
          <>
            <h5>{error?.toString()}</h5>
            <details>{errorInfo?.componentStack}</details>
          </>
        )}
      </div>
    </Wrapper>
  )
}
